// src/components/locations/forms/CountryForm.tsx
import React from "react";

import type { LocationCountry } from "@/api/features/locations/types";

import {
  AdvancedSection,
  FormSection,
} from "@/pages/locations/components/form/FormSection";
import { FormField } from "@/pages/locations/components/form/FormField";
import { controlClass } from "@/pages/locations/components/form/controlClass";
import {
  FormActions,
  FormError,
} from "@/pages/locations/components/form/FormActions";
import { RecordMeta } from "@/pages/locations/components/form/RecordMeta";
import { useLocationForm } from "@/pages/locations/components/form/useLocationForm";
import {
  apiErrorSummary,
  collectErrors,
  iso2Rule,
  requiredText,
} from "@/pages/locations/formErrors";

export type CountryFormValues = {
  iso2: string;
  name: string;
  nameNorm: string;
};

type Props = {
  mode: "create" | "edit";
  value?: LocationCountry;
  isSubmitting: boolean;
  /** Raw mutation error — 422 field detail is mapped onto the inputs. */
  error?: unknown;
  onSubmit: (values: CountryFormValues, options: { addAnother: boolean }) => void;
  onCancel: (isDirty: boolean) => void;
  /** Bumped by the page after "Save & add another" to clear the form. */
  resetSignal?: number;
};

const EMPTY: CountryFormValues = { iso2: "", name: "", nameNorm: "" };

/** Mirrors the backend fallback, so the hint shows what will actually be stored. */
function derivedAlias(name: string): string {
  return name.trim().toLowerCase();
}

const CountryForm: React.FC<Props> = ({
  mode,
  value,
  isSubmitting,
  error,
  onSubmit,
  onCancel,
  resetSignal = 0,
}) => {
  const initial = React.useMemo<CountryFormValues>(
    () =>
      value
        ? {
            iso2: String(value.iso2 ?? "").toUpperCase(),
            name: value.name ?? "",
            nameNorm: value.nameNorm ?? "",
          }
        : EMPTY,
    [value]
  );

  const validate = React.useCallback(
    (candidate: CountryFormValues) =>
      collectErrors({
        // ISO2 is the primary key — set once, read-only afterwards.
        iso2: mode === "create" ? iso2Rule(candidate.iso2) : undefined,
        name: requiredText(candidate.name, "Country name"),
      }),
    [mode]
  );

  const { values, setField, errorFor, isDirty, formRef, handleSubmit, resetTo } =
    useLocationForm<CountryFormValues>({
      initial,
      validate,
      idPrefix: "country",
      error,
      onValid: (v, options) =>
        onSubmit(
          {
            iso2: v.iso2.trim().toUpperCase(),
            name: v.name.trim(),
            nameNorm: v.nameNorm.trim(),
          },
          options
        ),
    });

  React.useEffect(() => {
    if (resetSignal > 0) resetTo(EMPTY);
  }, [resetSignal, resetTo]);

  return (
    <form
      ref={formRef}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(false);
      }}
    >
      <FormError message={apiErrorSummary(error)} />

      <FormSection
        title="Country details"
        description="Countries sit at the top of the location tree — regions and cities are filed under them."
      >
        <FormField
          id="country-iso2"
          label="Country code"
          required={mode === "create"}
          error={errorFor("iso2")}
          hint={
            mode === "create"
              ? "Two-letter ISO code — IN for India, AE for United Arab Emirates."
              : "This code identifies the country everywhere and cannot be changed."
          }
        >
          {(a) =>
            mode === "create" ? (
              <input
                {...a}
                value={values.iso2}
                onChange={(e) => setField("iso2", e.target.value.toUpperCase())}
                disabled={isSubmitting}
                maxLength={2}
                autoFocus
                placeholder="IN"
                className={controlClass(
                  errorFor("iso2"),
                  "w-28 font-mono uppercase tracking-[0.3em]"
                )}
              />
            ) : (
              <div className="inline-flex h-10 items-center rounded-lg border border-gray-200 bg-gray-50 px-4 font-mono text-sm tracking-[0.3em] text-gray-700">
                {values.iso2}
              </div>
            )
          }
        </FormField>

        <FormField
          id="country-name"
          label="Country name"
          required
          error={errorFor("name")}
          hint="The name staff and customers see wherever this country appears."
        >
          {(a) => (
            <input
              {...a}
              value={values.name}
              onChange={(e) => setField("name", e.target.value)}
              disabled={isSubmitting}
              autoFocus={mode === "edit"}
              placeholder="India"
              className={controlClass(errorFor("name"))}
            />
          )}
        </FormField>
      </FormSection>

      <AdvancedSection description="Search matching — safe to skip.">
        <FormField
          id="country-nameNorm"
          label="Search alias"
          error={errorFor("nameNorm")}
          hint={
            values.nameNorm.trim()
              ? "Used when matching this country against supplier and booking data."
              : `Leave blank and “${derivedAlias(values.name) || "the country name"}” is generated for you.`
          }
        >
          {(a) => (
            <input
              {...a}
              value={values.nameNorm}
              onChange={(e) => setField("nameNorm", e.target.value)}
              disabled={isSubmitting}
              placeholder={derivedAlias(values.name) || "india"}
              className={controlClass(errorFor("nameNorm"))}
            />
          )}
        </FormField>
      </AdvancedSection>

      {mode === "edit" && value && <RecordMeta record={value} />}

      <FormActions
        submitLabel={mode === "create" ? "Create country" : "Save changes"}
        submittingLabel="Saving…"
        isSubmitting={isSubmitting}
        onCancel={() => onCancel(isDirty)}
        onSaveAndAddAnother={mode === "create" ? () => handleSubmit(true) : undefined}
      />
    </form>
  );
};

export default CountryForm;
